import * as z from 'zod'
import type { ContentBlockFormValues, ContentBlockType } from './types'

/**
 * The style's natural title alignment, used to seed the alignment control so
 * it reflects the current look. Centered styles default to 'center'; all
 * others to 'left'. Mirrors the portal's per-style fallbacks.
 *
 * Shared by `ContentBlockFormBody` (form defaults) AND `buildContentBlockVariables`
 * (payload construction) so the two can't drift.
 */
export function naturalTitleAlign(
  style?: string | null
): 'left' | 'center' | 'right' {
  return style === '1_column_title_top' ||
    style === '2_columns_title_top_center'
    ? 'center'
    : 'left'
}

/**
 * Data-driven block types whose mutation pair differs (`linked_items` +
 * `createLinkedContentBlock`/`updateLinkedContentBlock`) from the plain
 * `createContentBlock`/`updateContentBlock` used by every other type. Shared
 * by the immediate (Dialog) save path and the page editor's deferred flush so
 * both pick the same mutation for a given type.
 */
export function isLinkedContentBlockType(type: ContentBlockType): boolean {
  return (
    type === 'photo_links' ||
    type === 'category_tiles' ||
    type === 'product_grid' ||
    type === 'page_grid'
  )
}

/**
 * Fold per-language inline title overrides on top of a `content_block_profiles`
 * array (or anything shaped like one). PURE — used by BOTH the page editor's
 * flush (`PageDetails.flushBlockDraft`, step 1's `name` overlay) AND the
 * settings form's seed (`ContentBlockFormBody`, so reopening a block's
 * settings panel shows the SAME effective title the flush would actually
 * send, instead of a stale server value) so the two precedence computations
 * can't drift apart.
 */
export function overlayProfileNames<T extends { language: string; name?: string }>(
  profiles: T[],
  nameByLanguage: Record<string, string>
): T[] {
  return profiles.map(p =>
    nameByLanguage[p.language] !== undefined
      ? { ...p, name: nameByLanguage[p.language] }
      : p
  )
}

/**
 * Map a `GET_CONTENT_BLOCK`-shaped response into `ContentBlockFormValues`, for
 * feeding into `buildContentBlockVariables` when DUPLICATING a block (the
 * Reassign modal's "Duplicate" action in `ContentBlockSelector`). Field-for-
 * field mirrors the `serverValues` object the settings forms
 * (`ContentBlockFormBody`/`ContentBlockForm`) build to SEED their own edit
 * form, so a duplicate reproduces the source's config exactly the way an
 * untouched re-save of the original would. Deliberately does NOT replicate
 * those forms' per-language placeholder-profile synthesis (filling in every
 * `allLanguages` entry) — duplication only needs to copy whichever profiles
 * already exist, not seed empty ones for an editor UI. Profile/linked-item
 * `id`s are always omitted so the create mutation makes fresh rows instead of
 * referencing the source's.
 */
export function buildDuplicateFormValues(
  source: any
): ContentBlockFormValues {
  const config = (source?.config ?? {}) as Record<string, any>
  return {
    content_block_profiles: (source?.content_block_profiles ?? []).map(
      (p: any) => ({
        name: p.name ?? '',
        description: p.description ?? '',
        description_format: p.description_format ?? 'plain',
        link: p.link ?? '',
        cta_label: p.cta_label ?? '',
        cta_link: p.cta_link ?? '',
        language: p.language,
      })
    ),
    content_block_type: source?.type,
    style: source?.style || undefined,
    images: source?.main_image ? [source.main_image.src] : [],
    gallery_images: (source?.gallery_images ?? []).map(
      (img: any) => img.src
    ),
    // Project to `{ id }` only. `source.grid_product_containers` comes straight
    // out of an Apollo query result, so each entry also carries `__typename`
    // (and would carry any other queried fields) — the picker's field shape is
    // `{ id: number }[]`, and `buildContentBlockVariables` reduces this to the
    // ordered `grid_products` id list at submit time. Unlike the settings
    // forms' own submit path, duplication never runs these values through
    // `contentBlockFormSchema` (which would otherwise strip this for free), so
    // it has to be done here. Deliberately NOT sourced from
    // `source.product_containers` — that relation is PDP attachment, which
    // duplication never copies (a duplicate must not silently attach itself to
    // the source's product pages).
    grid_product_picks: (source?.grid_product_containers ?? []).map(
      (p: any) => ({ id: p.id })
    ),
    video_link: source?.video_link || '',
    video_type: source?.video_type || undefined,
    video_autoplay: false,
    video_loop: false,
    video_controls: config?.video_controls ?? true,
    default_margins: true,
    object_fit_cover: false,
    extra_css: source?.extra_css ? JSON.stringify(source.extra_css) : '',
    text_color: source?.text_color || '',
    background_color: source?.background_color || '',
    link_new_tab: source?.link_new_tab || false,
    link_page_id: source?.link_page?.id || '',
    cta_new_tab: source?.cta_new_tab || false,
    cta_link_page_id:
      source?.cta_link_page_id || source?.cta_link_page?.id || '',
    cta_link_type: source?.cta_link_type ?? null,
    cta_link_category_id: source?.cta_link_category?.id ?? null,
    linked_items: (source?.linked_items ?? []).map((item: any) => ({
      title: item.title,
      link: item.link,
      arrangement: item.arrangement,
      image: item.image?.src || '',
    })),
    selected_category_ids: config?.category_ids ?? [],
    product_grid_mode:
      source?.type === 'product_grid' ? config?.mode || 'newest' : 'newest',
    product_grid_max_products: config?.max_products || 8,
    product_grid_tag_ids:
      source?.type === 'product_grid' ? config?.tag_ids ?? [] : [],
    page_grid_mode:
      source?.type === 'page_grid' ? config?.mode || 'manual' : 'manual',
    page_grid_max_pages: config?.max_pages || 8,
    page_grid_items_per_row: config?.items_per_row || 4,
    selected_page_ids: config?.page_ids ?? [],
    page_grid_parent_id: config?.parent_id || '',
    page_grid_show_tags: config?.show_tags ?? false,
    email_signup_delivery: config?.delivery || 'email',
    email_signup_notify_email: config?.notify_email || '',
    pdf_flipbook_pdf_url: config?.pdf_url ?? null,
    pdf_flipbook_manifest_url: config?.manifest_url ?? null,
    pdf_flipbook_show_download_button: Boolean(config?.show_download_button),
    // Absent/unknown values must mean 'flipbook' (backward compat: existing
    // blocks predate this key) — never trust anything but the literal 'plain'.
    pdf_flipbook_viewer_mode: config?.viewer_mode === 'plain' ? 'plain' : 'flipbook',
    titleAlignment: config?.titleAlignment ?? naturalTitleAlign(source?.style),
    hideTitle: Boolean(config?.hideTitle),
    textOnImageDisplay: config?.display ?? 'full_width',
    mediaRatio: config?.mediaRatio ?? '50_50',
  }
}

/**
 * Full validation schema for the content-block settings form. SHARED by:
 *  - `ContentBlockFormBody`'s `zodResolver` (immediate-save/Dialog path AND
 *    the staged path's inline `form.trigger()` field errors, both unchanged)
 *  - `PageDetails.flushBlockDraft` (staged path: validates + filters the RAW
 *    staged draft at flush time, immediately before `buildContentBlockVariables`)
 *
 * The `content_block_profiles` transform below filters out placeholder
 * profiles (no name/cta/description filled). That filtered/parsed OUTPUT
 * must only ever reach a PAYLOAD (i.e. `buildContentBlockVariables`), never
 * be fed back into `form.reset()` — filtering collapses the array and
 * breaks the index alignment the settings form's field paths depend on
 * (`content_block_profiles.${languageId}.*`, keyed off `LanguageContext`'s
 * positional `languageId`). Staging (`ContentBlockFormBody`'s staging
 * effect) therefore always keeps the RAW, unfiltered `form.getValues()` —
 * this schema is applied to that raw draft only once, at flush time.
 */
export const contentBlockFormSchema = z
  .object({
    content_block_profiles: z
      .array(
        z.object({
          id: z.number().optional(),
          name: z.string().optional(),
          description: z.string().optional(),
          description_format: z.enum(['plain', 'markdown']).optional().default('plain'),
          link: z.string().optional().default(''),
          cta_label: z.string().optional().default(''),
          cta_link: z.string().optional().default(''),
          language: z.string(),
        })
      )
      .transform(items =>
        items.filter(
          item =>
            item.name?.trim() ||
            item.cta_label?.trim() ||
            item.cta_link?.trim() ||
            item.description?.trim()
        )
      ),
    content_block_type: z.enum([
      'only_text',
      'only_image',
      'text_and_image',
      'only_video',
      'text_and_video',
      'gallery',
      'button',
      'photo_links',
      'category_tiles',
      'product_grid',
      'page_grid',
      'email_signup',
      'pdf_flipbook',
    ]),
    style: z
      .enum([
        '1_column_title_top',
        '2_columns_title_top_center',
        '2_columns_1_3',
        '3_columns_title_left',
        'masonry',
        'spread_harmony',
        'scroll_rhythm',
        'gallery_side_by_side',
        'side_by_side',
        'text_on_image',
        'image_left',
        'outline',
        'filled',
      ])
      .optional(),
    images: z.array(z.string()),
    gallery_images: z.array(z.string()),
    // Optional + defaulted (mirrors `product_grid_tag_ids`) so a reset that
    // doesn't set this explicitly (e.g. an older staged draft) still parses,
    // rather than silently failing `contentBlockFormSchema.safeParse` for
    // every block type.
    grid_product_picks: z
      .array(z.object({ id: z.number() }))
      .optional()
      .default([]),
    video_link: z.string().optional(),
    video_type: z.enum(['youtube', 'vimeo', 'uploaded']).optional(),
    video_autoplay: z.boolean(),
    video_loop: z.boolean(),
    video_controls: z.boolean(),
    default_margins: z.boolean(),
    object_fit_cover: z.boolean(),
    extra_css: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val || val.trim() === '') return true
          try {
            JSON.parse(val)
            return true
          } catch {
            return false
          }
        },
        { message: 'Invalid JSON format' }
      ),
    text_color: z.string().optional(),
    background_color: z.string().optional(),
    link_new_tab: z.boolean(),
    link_page_id: z.string(),
    cta_new_tab: z.boolean(),
    cta_link_page_id: z.string(),
    cta_link_type: z.enum(['cms_page', 'category', 'store']).nullable().optional(),
    cta_link_category_id: z.number().nullable().optional(),
    linked_items: z.array(
      z.object({
        id: z.number().optional(),
        title: z.string(),
        link: z.string(),
        arrangement: z.number(),
        image: z.string(),
        // Uploaded image's row id — when set, the update resolver `connect`s
        // the existing Image row instead of creating a new one from `image`
        // (a data URL/URL string). Without this in the schema, `z.object`'s
        // default unknown-key stripping silently dropped it from BOTH save
        // paths (Dialog `zodResolver` parse and the page editor's
        // `flushBlockDraft` `safeParse`) even though it was already seeded/
        // sent — see `ContentBlockFormBody`'s population effect and
        // `buildContentBlockVariables` below. Mirrors A (`ContentBlockForm.tsx`).
        imageId: z.number().nullable().optional(),
      })
    ),
    selected_category_ids: z.array(z.number()),
    product_grid_mode: z.enum(['newest', 'manual']).optional().default('newest'),
    product_grid_max_products: z.number().min(1).max(8).optional().default(8),
    product_grid_tag_ids: z.array(z.number()).optional().default([]),
    page_grid_mode: z.enum(['manual', 'children']).optional().default('manual'),
    page_grid_max_pages: z.number().min(1).max(24).optional().default(8),
    page_grid_items_per_row: z.number().min(2).max(6).optional().default(4),
    selected_page_ids: z.array(z.string()),
    page_grid_parent_id: z.string().optional(),
    page_grid_show_tags: z.boolean().optional().default(false),
    email_signup_delivery: z
      .enum(['email', 'mailerlite'])
      .optional()
      .default('email'),
    email_signup_notify_email: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val || val.trim() === '') return true
          return z.string().email().safeParse(val).success
        },
        { message: 'Invalid email address' }
      ),
    pdf_flipbook_pdf_url: z.string().nullable().optional(),
    pdf_flipbook_manifest_url: z.string().nullable().optional(),
    pdf_flipbook_show_download_button: z.boolean().optional(),
    // Renders as Flipbook vs the browser's native PDF viewer. Optional/
    // defaulted so it's declared in the schema's output shape — without this,
    // z.object's unknown-key stripping would silently drop it on every parse
    // (Dialog zodResolver AND PageDetails.flushBlockDraft's safeParse), and
    // the payload would always fall back to 'flipbook' even when the form
    // holds 'plain'. Mirrors the imageId comment above.
    pdf_flipbook_viewer_mode: z.enum(['flipbook', 'plain']).optional().default('flipbook'),
    titleAlignment: z.enum(['left', 'center', 'right']).optional(),
    hideTitle: z.boolean().optional().default(false),
    textOnImageDisplay: z
      .enum(['full_width', 'content_width', 'text_width'])
      .optional(),
    mediaRatio: z.enum(['50_50', '60_40', '40_60']).optional(),
  })
  .superRefine((data, ctx) => {
    // Data-driven blocks (grids / linked items) configure themselves from the
    // selected pages/products/categories, so a display name is optional. Every
    // other type still requires at least one filled language profile.
    const nameOptionalTypes = [
      'photo_links',
      'category_tiles',
      'product_grid',
      'page_grid',
    ]
    if (
      !nameOptionalTypes.includes(data.content_block_type) &&
      data.content_block_profiles.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one language profile needs to be filled',
        path: ['content_block_profiles', 0, 'name'],
      })
    }
  }) satisfies z.ZodType<ContentBlockFormValues>

/**
 * Turn a failed `contentBlockFormSchema.safeParse(...)` into ONE short,
 * human-readable sentence for a toast — never the raw `ZodError`. Used by
 * `PageDetails.flushBlockDraft` to name WHY a block's flush was refused,
 * alongside the block's name, in the existing save-what-works failure toast.
 */
export function describeContentBlockValidationError(error: z.ZodError): string {
  const issue = error.issues[0]
  if (!issue) return 'invalid values'
  const field = issue.path[0]
  if (field === 'content_block_profiles') {
    return issue.message.toLowerCase().includes('language profile')
      ? issue.message
      : `title/description fields: ${issue.message}`
  }
  if (field === 'extra_css') return `Custom Styles: ${issue.message}`
  if (field === 'email_signup_notify_email')
    return `Recipient Email: ${issue.message}`
  if (field === 'pdf_flipbook_pdf_url') return `PDF Flipbook: ${issue.message}`
  return issue.message
}

export interface BuildContentBlockVariablesOptions {
  /**
   * Omit `description` from every profile payload entry (page editor: the
   * description is edited inline in the full-size preview instead of via this
   * form). Mirrors `ContentBlockFormBody`'s `hideDescription` prop.
   */
  hideDescription: boolean
  /**
   * The block's raw `config` JSON as currently known (from `GET_CONTENT_BLOCK`
   * at form-mount time for the immediate-save path, or the page editor's
   * locally-held block for the deferred flush). Spread FIRST when rebuilding
   * `config` so keys the form doesn't manage (grid keys, category_ids, …)
   * survive a save that doesn't touch them.
   */
  rawConfig: Record<string, unknown>
  /**
   * True when this payload is for a CREATE mutation (brand-new block OR a
   * duplicate — see `ContentBlockSelector.handleDuplicate`), false for an
   * UPDATE of an existing block. Affects ONLY the `product_grid` branch
   * below: `grid_products` is normally sent solely in 'manual' mode so an
   * UPDATE in another mode can't wipe an existing manual picks table it
   * isn't touching. A CREATE has no existing table to protect, so it must
   * always send whatever picks the form carries — otherwise duplicating a
   * dormant 'newest'-mode block (which still copies `grid_product_picks`
   * from its source, see `buildDuplicateFormValues`) would silently drop
   * them on the copy.
   */
  isCreate: boolean
}

/**
 * Build the mutation `variables` payload for a content-block create/update
 * from validated form values. PURE — no network calls, no component refs — so
 * the immediate (Dialog) save path (`ContentBlockFormBody.onSubmit`) and the
 * page editor's deferred flush (`PageDetails.onSave`) construct an IDENTICAL
 * payload from identical inputs and can't drift apart.
 */
export function buildContentBlockVariables(
  values: ContentBlockFormValues,
  { hideDescription, rawConfig, isCreate }: BuildContentBlockVariablesOptions
): Record<string, unknown> {
  const variables: Record<string, unknown> = {
    content_block_profiles: values.content_block_profiles.map(p => ({
      id: p.id,
      name: p.name,
      // When hideDescription is set (page editor), omit BOTH the `description`
      // and `description_format` keys entirely so the create/update mutation
      // doesn't send stale values and clobber the description (and its format)
      // that's edited inline in the preview via `EditableDescription` /
      // `MarkdownEditor` (which force-sets `description_format: 'markdown'`
      // through a separate `UPDATE_CONTENT_BLOCK_PROFILE` call — see
      // `PageDetails.tsx`). Sending a stale seeded `description_format`
      // alongside a dropped `description` would re-flag an inline-markdown
      // description as 'plain', and the storefront would then render raw
      // markdown source. Prisma leaves the existing value untouched on update
      // / uses the default on create when the key is absent.
      ...(hideDescription
        ? {}
        : {
            description: p.description,
            description_format: p.description_format ?? 'plain',
          }),
      link: p.link || null,
      cta_label: p.cta_label || null,
      cta_link: p.cta_link || null,
      language: p.language,
    })),
    content_block_type: values.content_block_type,
    style: values.style || null,
    images: values.images,
    gallery_images: values.gallery_images,
    // `selected_products` (PDP attachment — "show this block on these
    // products' pages") is DELIBERATELY never sent from this form. It's
    // managed on the product side via `updateProductContainerContentBlocks`.
    // Omitting the key here means the mutation variable is undefined, which
    // Apollo omits from the request — the resolver reads that as "untouched"
    // (the arg is optional), so a save here can never wipe PDP attachments.
    video_link: values.video_link || null,
    video_type: values.video_type || null,
    video_autoplay: values.video_autoplay,
    video_loop: values.video_loop,
    default_margins: values.default_margins,
    object_fit_cover: values.object_fit_cover,
    extra_css: values.extra_css ? JSON.parse(values.extra_css) : null,
    text_color: values.text_color || null,
    background_color: values.background_color || null,
    link_new_tab: values.link_new_tab,
    link_page_id: values.link_page_id || null,
    cta: {
      new_tab: values.cta_new_tab,
      link_page_id: values.cta_link_page_id || null,
      // Legacy rows saved before `cta_link_type` existed only have
      // `cta_link_page_id` set — fall back to `cms_page` so an untouched
      // save of a pre-existing CMS-page CTA doesn't send `link_type: null`
      // (which would disconnect the page FK). Mirrors the portal's own
      // `resolveCtaHref` legacy fallback.
      link_type:
        values.cta_link_type ?? (values.cta_link_page_id ? 'cms_page' : null),
      link_category_id: values.cta_link_category_id ?? null,
    },
  }

  const isLinkedType = isLinkedContentBlockType(values.content_block_type)

  if (isLinkedType) {
    variables.linked_items =
      values.linked_items?.map((item, i) => ({
        ...(item.id && { id: item.id }),
        title: item.title,
        link: item.link,
        arrangement: i,
        image: item.image || null,
        imageId: item.imageId || null,
      })) ?? []
    // Always emit `category_ids` (even when empty) so this form-managed key
    // wins over the spread-first loaded config in the merge below — otherwise
    // clearing all categories could not persist (the old ids would survive).
    variables.config = { category_ids: values.selected_category_ids ?? [] }
  }

  if (values.content_block_type === 'product_grid') {
    variables.config = {
      mode: values.product_grid_mode || 'newest',
      max_products: values.product_grid_max_products || 8,
      // Always emit `tag_ids` (even when empty) so this form-managed key wins
      // over the spread-first loaded config in the merge below — otherwise
      // clearing all tags could not persist (the old ids would survive).
      tag_ids: values.product_grid_tag_ids ?? [],
    }
    // `grid_products` (ordered product container ids) is sent in manual mode
    // ALWAYS, and in every other mode only on CREATE. On UPDATE it's omitted
    // outside manual mode so the grid table stays untouched — switching from
    // 'manual' to 'newest' and back (or just re-saving other fields while in
    // 'newest' mode) must not wipe the manual picks. CREATE has no existing
    // table to protect, so it always sends whatever picks the form carries —
    // otherwise duplicating a dormant 'newest'-mode block would silently drop
    // the picks it copied from its source (see `isCreate` doc above). Deduped
    // defensively; the backend also dedupes (keeps first occurrence).
    if (isCreate || (values.product_grid_mode || 'newest') === 'manual') {
      const seen = new Set<number>()
      const ids: number[] = []
      for (const pick of values.grid_product_picks ?? []) {
        if (!seen.has(pick.id)) {
          seen.add(pick.id)
          ids.push(pick.id)
        }
      }
      variables.grid_products = ids
    }
  }

  if (values.content_block_type === 'page_grid') {
    variables.config = {
      mode: values.page_grid_mode || 'manual',
      max_pages: values.page_grid_max_pages || 8,
      page_ids:
        values.page_grid_mode === 'manual' ? values.selected_page_ids : [],
      parent_id:
        values.page_grid_mode === 'children'
          ? values.page_grid_parent_id || null
          : null,
      show_tags: values.page_grid_show_tags ?? false,
      items_per_row: values.page_grid_items_per_row || 4,
    }
  }

  if (values.content_block_type === 'email_signup') {
    variables.config = {
      delivery: values.email_signup_delivery || 'email',
      notify_email:
        values.email_signup_delivery === 'mailerlite'
          ? null
          : values.email_signup_notify_email || null,
    }
  }

  if (values.content_block_type === 'pdf_flipbook') {
    variables.config = {
      pdf_url: values.pdf_flipbook_pdf_url || null,
      manifest_url: values.pdf_flipbook_manifest_url || null,
      show_download_button: values.pdf_flipbook_show_download_button ?? false,
      // Absent/unknown ⇒ 'flipbook' — see `pdf_flipbook_viewer_mode` schema
      // comment. Always emit explicitly (even the default) so this form-
      // managed key wins over the spread-first loaded config below.
      viewer_mode: values.pdf_flipbook_viewer_mode === 'plain' ? 'plain' : 'flipbook',
    }
  }

  // Block-level title settings live in `config`. Always send a MERGED config:
  // the loaded config FIRST, then whatever grid/category keys the branches
  // above built, then the title keys — so no stored key (mode, page_ids,
  // parent_id, show_tags, category_ids, …) is dropped by the custom mutations.
  // Buttons render the name as a label (no title), so skip the title keys.
  const isButtonType = values.content_block_type === 'button'
  if (!isButtonType) {
    const builtConfig =
      (variables.config as Record<string, unknown> | null) ?? {}
    variables.config = {
      ...rawConfig,
      ...builtConfig,
      titleAlignment: values.titleAlignment ?? naturalTitleAlign(values.style),
      hideTitle: values.hideTitle ?? false,
      display: values.textOnImageDisplay ?? 'full_width',
      mediaRatio: values.mediaRatio ?? '50_50',
      video_controls: values.video_controls ?? true,
    }
  }

  return variables
}
